/**
 * ReportContext — глобальная точка входа для жалоб (CORE-26).
 *
 * Любой экран/карточка вызывает openReport({ targetType, targetId }) —
 * провайдер рендерит ReportModal поверх навигатора и после успешной
 * отправки показывает BrandToast «Спасибо. Ваша жалоба отправлена на
 * рассмотрение.». Гости уводятся в логин через AuthCTA (BE отвечает 401),
 * после входа модал открывается автоматически.
 *
 * Исключение: поверхности, живущие внутри уже открытого RN Modal
 * (CommentsModal), рендерят ReportModal локально — глобальный оверлей на
 * iOS оказался бы под их модалом.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useAuthCTA } from './AuthCTAContext';
import { ReportModal, ReportTarget } from '../components/ReportModal';
import { BrandToast } from '../components/BrandToast';

export const REPORT_SENT_MESSAGE = 'Спасибо. Ваша жалоба отправлена на рассмотрение.';

interface ReportContextType {
  openReport: (target: ReportTarget) => void;
}

const ReportContext = createContext<ReportContextType>({
  openReport: () => {},
});

export const useReport = () => useContext(ReportContext);

export const ReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { showAuthCTA } = useAuthCTA();
  const [target, setTarget] = useState<ReportTarget | null>(null);
  const [toastVisible, setToastVisible] = useState(false);

  const openReport = useCallback((t: ReportTarget) => {
    if (!isAuthenticated) {
      showAuthCTA('gated_action', 'Чтобы отправить жалобу, войдите в аккаунт.', () => setTarget(t));
      return;
    }
    setTarget(t);
  }, [isAuthenticated, showAuthCTA]);

  const handleSubmitted = useCallback(() => {
    setTarget(null);
    setToastVisible(true);
  }, []);

  return (
    <ReportContext.Provider value={{ openReport }}>
      {children}
      <ReportModal
        target={target}
        onClose={() => setTarget(null)}
        onSubmitted={handleSubmitted}
      />
      <BrandToast
        visible={toastVisible}
        onHide={() => setToastVisible(false)}
        message={REPORT_SENT_MESSAGE}
      />
    </ReportContext.Provider>
  );
};
